import React, { useState, useEffect } from "react";
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import pdfjsLib from "pdfjs-dist/package.json";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import Loading from "components/loading/Loading";
import useIsPhone from "features/files/use-is-phone";
import { fetchFileUrl } from "services/upload-file.service";
import { btnSecondaryCls } from "components/tw/classes";

const PdfModalViewer = ({
  id,
  companyId,
}: {
  id: string;
  companyId: string;
}) => {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [pdfBlob, setPdfBlob] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const pdfUrl = await fetchFileUrl(id, companyId);
        setPdfBlob(pdfUrl);
      } catch (error) {
        console.error("Error fetching PDF:", error);
      } finally {
        setLoading(false);
      }
    }
    if (id && companyId) {
      fetchData();
    }
  }, [id, companyId]);

  if (isLoading) {
    return <Loading />;
  }

  const viewer = pdfBlob && <Viewer fileUrl={pdfBlob} defaultScale={SpecialZoomLevel.PageWidth} />;
  // Only a phone gets the link to the file.
  const openPdfHref = isPhone ? pdfBlob : null;

  return (
    <Worker
      workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`}
    >
      {/*
        The page fills the width of this box and the viewer zooms again whenever that width changes. So the box
        has its own height and the pages scroll inside it: in a modal that grew with the pages, the modal's
        scrollbar came and went while scrolling and every time the pages were redrawn at another zoom.
        Inside a holder with a height of its own (the queue sheet) the box takes that height instead of 80dvh
        (dvh, not vh: on a phone vh counts the space under the browser's bars).
        48rem keeps the page at about 1.25 times its real size, so a large modal does not blow it up.
        On a phone the page is as wide as the screen and its text is small; the link under it opens the file in
        the browser's own viewer, where it can be zoomed. Only then is the box a column, so from 768 px up the
        viewer sits directly in the box, as it always did.
      */}
      <div
        className={`mx-auto h-[80dvh] max-h-full w-full max-w-[48rem] ${openPdfHref ? "flex flex-col" : ""}`}
      >
        {openPdfHref ? (
          <>
            <div className="min-h-0 flex-1">{viewer}</div>
            <a
              href={openPdfHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnSecondaryCls} mt-2 shrink-0`}
            >
              <ExternalLink className="size-4" aria-hidden="true" />
              {t("open_pdf")}
            </a>
          </>
        ) : (
          viewer
        )}
      </div>
    </Worker>
  );
};

export default PdfModalViewer;
